import { Asset } from 'app-lib/assetUtils'
import providerUtils from 'app-lib/providerUtils'
import _ from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import { chainConfigs, isChainSupported } from '../config/appConfig'
import RootStore from './RootStore'

export interface ITokenBalance {
  token: Asset
  amount: BigNumber
}

interface IUserWalletProps {
  rootStore: RootStore
  balances: ITokenBalance[]
  pending: boolean
  getBalancesPending: boolean
  error: Error | null
  nativeBalance: ITokenBalance
}

/**
 * For DEV:
 * `rootStore.userWallet.updateBalances([{token: 'OOKI', amount: new BigNumber(1000)}])`
 */
export default class UserWallet implements IUserWalletProps {
  rootStore: RootStore
  balances: ITokenBalance[] = []
  pending = false
  getBalancesPending = false
  error: Error | null = null
  nativeBalance: ITokenBalance = { token: Asset.ETH, amount: new BigNumber(0) }

  public get indexedByAsset(): Partial<Record<Asset, ITokenBalance>> {
    return _.keyBy(this.balances, (balance) => balance.token)
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: keyof IUserWalletProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: Partial<IUserWalletProps>) {
    Object.assign(this, props)
  }

  public clearError() {
    this.error = null
  }

  findTokenBalance(asset: Asset) {
    const tokenBalance = this.balances.find((balance) => balance.token === asset)
    return tokenBalance ? tokenBalance.amount : new BigNumber(0)
  }

  findTokensBalances(assets: Asset[]) {
    return assets.reduce((acc, asset) => {
      const balance = this.indexedByAsset[asset]
      acc.push(balance || { token: asset, amount: new BigNumber(0) })
      return acc
    }, [] as { token: Asset; amount: BigNumber }[])
  }

  /**
   * Updates the current wallet by completely.
   *
   * Note it's updating the balances by replacing the current array of balances
   * @param newBalances `{token: Asset, amount: BigNumber}`
   */
  updateBalances(newBalances: ITokenBalance[]) {
    this.balances = _.unionBy(newBalances, this.balances, 'token')
  }

  /**
   * Substract a certain amount from a single token balance
   * @param token Asset
   * @param amount Amount to substract
   * @returns new balance OR false if the token balance is 0 or does not exist in the wallet
   */
  substractFromToken(token: Asset, amount: BigNumber) {
    const tokenBalance = this.findTokenBalance(token)
    if (tokenBalance.isZero()) {
      return false
    }
    const newBalance = tokenBalance.minus(amount)
    this.updateBalances([{ token, amount: newBalance }])
    return newBalance
  }

  /**
   * Balance of ETH on Ethereum, BNB on BSC, MATIC on Polygon
   */
  async getNativeTokenBalance() {
    const fp = this.rootStore.fulcrumProvider
    const balance = await providerUtils.getEthBalance(fp)
    mobx.runInAction(() => {
      this.nativeBalance = {
        token: chainConfigs[fp.currentChainId].native.asset,
        amount: balance.div(10 ** 18),
      }
    })
    return this.nativeBalance
  }

  /**
   * Fetch balances from the blockchain; updates the wallet state and returns the balances
   * Note that balances are already divided by the decimal precision
   */
  async getBalances(tokens: Asset[]) {
    this.assign({ getBalancesPending: true })
    const fp = this.rootStore.fulcrumProvider
    const result = await providerUtils.getWalletBalances(fp, fp.currentAccount, tokens)
    this.updateBalances(result)
    this.assign({ getBalancesPending: false })
    return result
  }

  /**
   * @param amount eg: want to send 10 USDC; BigNumber(10)
   */
  async sendToken(asset: Asset, to: string, amount: BigNumber) {
    const { transactionStore } = this.rootStore
    let tx
    try {
      this.assign({ pending: true, error: null })
      const txSteps = providerUtils.sendErc20(this.rootStore.fulcrumProvider, asset, to, amount)
      tx = await transactionStore.run('transfer', txSteps)
      return tx
    } catch (err) {
      console.error(err)
      return tx
    } finally {
      this.assign({ pending: false })
    }
  }

  public clearData() {
    const { chainId } = this.rootStore.web3Connection
    this.balances = []
    // We use ETH as native token if the chain is not supported; it is not correct but avoids an issue
    const token =
      chainId && isChainSupported(chainId) ? chainConfigs[chainId].native.asset : Asset.ETH
    this.nativeBalance = {
      token,
      amount: new BigNumber(0),
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
  }
}
